import * as React from 'react'

import { SheetSectionCard } from '@/components/SheetSectionCard'
import type { PatientRecord } from '@/features/patients/types'
import { formatVeteranStatus } from '@/features/patients/utils/clientDisplay'
import type { ClientMetaRow } from './ClientMetaList'
import { ClientMetaList } from './ClientMetaList'

function identityRows(patient: PatientRecord): ClientMetaRow[] {
  return [
    { label: 'Gender', value: patient.gender },
    { label: 'Race', value: patient.race },
    { label: 'Ethnicity', value: patient.ethnicity },
    { label: 'Orientation', value: patient.sexual_orientation },
    { label: 'Language', value: patient.language },
    { label: 'County', value: patient.county_name },
  ]
}

function circumstanceRows(patient: PatientRecord): ClientMetaRow[] {
  return [
    { label: 'Veteran', value: formatVeteranStatus(patient.is_veteran) },
    { label: 'Housing', value: patient.housing_status },
    { label: 'Insurance', value: patient.insurance },
    { label: 'Primary payer', value: patient.primary_insurance },
    { label: 'UDS included', value: patient.uds_inc },
    { label: 'Income', value: patient.income },
  ]
}

/**
 * Demographics, socio-economic context and record flags in one card.
 *
 * Previously four stacked cards (Demographics / Insurance & Financial / Status
 * & Flags), which pushed everything below them off-screen for fields that are
 * read together. Two columns of short rows fit the same content in roughly a
 * third of the height.
 */
export function ClientDemographicsCard({
  patient,
}: {
  patient: PatientRecord
}) {
  const identity = React.useMemo(() => identityRows(patient), [patient])
  const circumstance = React.useMemo(() => circumstanceRows(patient), [patient])

  return (
    <SheetSectionCard
      title="Demographics & status"
      body={
        <div className="grid gap-x-3 gap-y-2 sm:grid-cols-2">
          <ClientMetaList rows={identity} />
          <ClientMetaList rows={circumstance} />
        </div>
      }
    />
  )
}
